import { useState, useEffect } from "react";
import { Search, Copy, Calendar, Building2, Truck, User, Mail, Phone, MapPin, FileText } from "lucide-react";
import axios from "axios";
import Swal from 'sweetalert2';

import API_BASE_URL from "../config";

export default function PurchaseOrder() {
    // ✅ NEW: Toggle between Quotation and Manual input mode
    const [inputMode, setInputMode] = useState("quotation"); // "quotation" | "manual"

    const [formData, setFormData] = useState({
        poNumber: "",
        poCreatedDate: new Date().toISOString().split('T')[0],
        referencePRId: 0,
        inventoryDestinationPRId: 0,
        paymentMode: "",
        tin: "",
        companyId: "",
        company_Address1: "",
        company_Address2: "",
        company_TIN: "",
        company_ContactNumber: "",
        company_Email: "",
        departmentId: "",
        locationId: "",
        vendorId: "",
        vendor_Address1: "",
        vendor_Address2: "",
        vendor_ContactPerson: "",
        vendor_ContactNumber: "",
        terms: "",
        shipTo_Location: "",
        shipTo_Address1: "",
        shipTo_Address2: "",
        shipTo_ContactPerson: "",
        shipTo_ContactNumber: "",
        deliveryDate: "",
        remarks: "",
        isVatInclusive: true,
        headerDiscount: 0,
        preparedBy: 0,
        status: 0,
        isPOPrinted: false,
        reprintCount: 0,
        receivingStatus: "Pending",
        isFromQuotation: 0,
        isNonVAT: false
    });

    const [approvalUsers] = useState([
        { id: 1, name: "Juan Dela Cruz", role: "Manager" },
        { id: 2, name: "Maria Santos", role: "Supervisor" },
        { id: 3, name: "Mark Reyes", role: "Accounting" },
    ]);

    const [selectedPRItems, setSelectedPRItems] = useState([]);
    const [prDetailsModal, setPrDetailsModal] = useState(false);
    const [prDetails, setPrDetails] = useState([]);
    const [checkedItems, setCheckedItems] = useState({});

    const calculateTotalDiscount = () => {
        if (!useDiscount) return 0;
        return poItems.reduce((sum, item) => {
            const lineTotal = item.qty * item.unitPrice;
            return sum + (lineTotal * item.detailDiscount) / 100;
        }, 0);
    };

    const handleQuotationSelect = async (quotation) => {
        setSelectedQuotation(quotation);
        const details = quotation.quotationDetails || quotation.items || [];

        if (details.length === 0) {
            await Swal.fire({ icon: 'info', title: 'No Items', text: 'This quotation has no line items.', confirmButtonText: 'OK' });
            return;
        }

        const mappedDetails = details.map(item => {
            const originalUom = uoms.find(u =>
                u.uom === (item.uom || item.UOM || "") ||
                u.id === (item.uomId || item.UomId || 0)
            );

            return {
                id: item.prDetailId || item.PRDetailId || 0,
                quotationDetailId: item.quotationDetailId || item.QuotationDetailId || 0,
                itemCode: item.itemCode || item.ItemCode || "",
                itemDescription: item.itemDescription || item.ItemDescription || "",
                materialFinish: item.specification || item.Specification || "",
                qty: item.qty || item.Qty || 0,
                uom: originalUom?.uom || item.uom || item.UOM || "",
                unitPrice: item.unitPrice || item.UnitPrice || 0,
                displayUsedQty: item.useQty || item.UseQty || 0,
                actualAvailable: (item.qty || item.Qty || 0) - (item.useQty || item.UseQty || 0),
                selectedQty: (item.qty || item.Qty || 0) - (item.useQty || item.UseQty || 0),
                approvedUsed: 0,
                pendingUsed: 0,
                remainingQty: (item.qty || item.Qty || 0) - (item.useQty || item.UseQty || 0),
            };
        });

        handleChange("referencePRId", quotation.prHeaderId || quotation.PRHeaderId || 0);
        handleChange("referencePRNo", quotation.quotationNo || quotation.QuotationNo || "");
        handleChange("inventoryDestinationPRNo", quotation.quotationNo || quotation.QuotationNo || "");

        // ✅ Ayos na — palaging override ng vendor from quotation
        // ✅ sa handleQuotationSelect, palitan ang setFormData block:
        setFormData(prev => ({
            ...prev,
            vendorId: quotation.supplierId || quotation.SupplierId || "",
            vendorName: quotation.supplierName || quotation.SupplierName || "",
            vendor_Address1: quotation.supplierAddress || quotation.SupplierAddress || "",
            vendor_ContactNumber: quotation.supplierContact || quotation.SupplierContact || "",
            vendor_Email: quotation.supplierEmail || quotation.SupplierEmail || "",
        }));

        setPrDetails(mappedDetails);
        const initialChecked = {};
        mappedDetails.forEach(item => { initialChecked[item.id] = true; });
        setCheckedItems(initialChecked);
        setShowQuotationModal(false);
        setPrDetailsModal(true);
    };

    const getPRStatus = (status) => {
        switch (status) {
            case 0: return { label: "Pending", className: "bg-gray-100 text-gray-700" };
            case 1: return { label: "In Progress", className: "bg-blue-100 text-blue-700" };
            case 2: return { label: "Approved", className: "bg-green-100 text-green-700" };
            case 3: return { label: "Rejected", className: "bg-red-100 text-red-700" };
            default: return { label: "Unknown", className: "bg-gray-200 text-gray-600" };
        }
    };

    const getDestinationLabel = (id) => {
        if (!id) return { label: "No Destination", className: "bg-gray-100 text-gray-500" };
        const dest = inventoryDestinations.find(d => d.destinationId === id);
        if (!dest) return { label: "Unknown Destination", className: "bg-gray-200 text-gray-600" };
        let type = "";
        if (dest.asset) type = "Asset";
        else if (dest.wms) type = "WMS";
        else if (dest.both) type = "Both";
        return {
            label: `${dest.destinationName} (${type})`,
            className: dest.asset ? "bg-green-100 text-green-700" : dest.wms ? "bg-blue-100 text-blue-700" : dest.both ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500",
        };
    };

    useEffect(() => {
        fetch(`${API_BASE_URL}api/InventoryDestination`)
            .then(res => res.json())
            .then(data => setInventoryDestinations(data))
            .catch(err => console.error(err));
    }, []);

    const [inventoryDestinations, setInventoryDestinations] = useState([]);
    const [showPRModal, setShowPRModal] = useState(false);
    const [prSearch, setPRSearch] = useState("");
    const [prList, setPrList] = useState([]);
    const [isPROpen, setIsPROpen] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/PR_Header`)
            .then(res => res.json())
            .then(data => setPrList(data))
            .catch(err => console.error("Error loading PR Headers:", err));
    }, []);

    const [uoms, setUoms] = useState([]);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/M_Uom`)
            .then((res) => res.json())
            .then((data) => setUoms(data))
            .catch((err) => console.error("UOM fetch error:", err));
    }, []);

    const [selectedApprovers, setSelectedApprovers] = useState([]);
    const toggleApprover = (id) => {
        setSelectedApprovers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const handleInputChange = (e, field) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const [companies, setCompanies] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [locations, setLocations] = useState([]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const [poItems, setPoItems] = useState([
        {
            id: 0, qty: 0, uom: "", itemDescription: "", specification: "",
            unitPrice: 0, detailDiscount: 0, serveStatus: "Pending",
            lastServedDate: new Date().toISOString(), chargingVisa: "",
            costEstimate: 0, totalReceivedQty: 0, referencePRDetailsId: 0
        }
    ]);

    const [isVatInclusive, setIsVatInclusive] = useState(false);
    const [useDiscount, setUseDiscount] = useState(false);
    const [isNonVat, setIsNonVat] = useState(true);

    const addNewItem = () => {
        setPoItems([...poItems, {
            id: 0, qty: 0, uom: "", itemDescription: "", specification: "",
            unitPrice: 0, detailDiscount: 0, serveStatus: "Pending",
            lastServedDate: new Date().toISOString(), chargingVisa: "",
            costEstimate: 0, totalReceivedQty: 0, referencePRDetailsId: 0
        }]);
    };

    const removeItem = (index) => {
        const updated = poItems.filter((_, i) => i !== index);
        setPoItems(updated);
    };

    const updateItem = (index, field, value) => {
        const updated = [...poItems];
        updated[index][field] = value;
        setPoItems(updated);
    };

    const calculateSubtotal = () => {
        return poItems.reduce((sum, item) => {
            const lineTotal = item.qty * item.unitPrice;
            const discount = useDiscount ? (lineTotal * item.detailDiscount) / 100 : 0;
            return sum + (lineTotal - discount);
        }, 0);
    };

    const calculateVAT = () => {
        if (isNonVat) return 0;
        if (!isVatInclusive) return 0;
        return calculateSubtotal() * 0.12;
    };

    const calculateTotal = () => {
        return calculateSubtotal() + calculateVAT();
    };

    const [activeTab, setActiveTab] = useState("details");
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [vendorPage, setVendorPage] = useState(1);

    const saveDraft = () => {
        const draft = {
            id: Date.now(), timestamp: new Date().toISOString(),
            formData, poItems, isVatInclusive, useDiscount, isNonVat
        };
        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        drafts.push(draft);
        localStorage.setItem('po_drafts', JSON.stringify(drafts));
        loadDrafts();
        Swal.fire({ icon: 'success', title: 'Draft Saved', text: `Draft saved successfully! PO: ${formData.poNumber}`, timer: 2000, showConfirmButton: false });
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        setSavedDrafts(drafts);
    };

    const loadDraft = (draft) => {
        setFormData(draft.formData);
        setPoItems(draft.poItems);
        setIsVatInclusive(draft.isVatInclusive);
        setUseDiscount(draft.useDiscount);
        setIsNonVat(draft.isNonVat);
        setShowDraftModal(false);
        Swal.fire({ icon: 'success', title: 'Draft Loaded', text: 'Draft loaded successfully!', timer: 2000, showConfirmButton: false });
    };

    const deleteDraft = async (draftId) => {
        const result = await Swal.fire({
            title: 'Delete Draft?', text: 'This will permanently delete the draft.', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;
        const drafts = JSON.parse(localStorage.getItem('po_drafts') || '[]');
        const filtered = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('po_drafts', JSON.stringify(filtered));
        loadDrafts();
        Swal.fire({ icon: 'success', title: 'Deleted', text: 'Draft deleted successfully!', timer: 2000, showConfirmButton: false });
    };

    useEffect(() => { loadDrafts(); }, []);

    const handleSubmit = async () => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.userId || 0;

            const headerPayload = {
                id: 0,
                poNumber: formData.poNumber,
                inventoryDestinationPRId: formData.inventoryDestinationPRNo || "",
                poCreatedDate: new Date(formData.poCreatedDate).toISOString(),
                referencePRId: parseInt(formData.referencePRId) || 0,
                companyId: parseInt(formData.companyId) || 0,

                // ✅ FIX: kung from quotation, gamitin ang supplierId/supplierName
                vendorId: inputMode === "quotation" && selectedQuotation
                    ? parseInt(selectedQuotation.supplierId || selectedQuotation.SupplierId || formData.vendorId) || 0
                    : parseInt(formData.vendorId) || 0,

                vendorName: inputMode === "quotation" && selectedQuotation
                    ? selectedQuotation.supplierName || selectedQuotation.SupplierName || formData.vendorName || ""
                    : formData.vendorName || "",

                terms: formData.terms,
                deliveryDate: new Date(formData.deliveryDate).toISOString(),
                shipTo_Location: formData.shipTo_Location,
                shipTo_Address1: formData.shipTo_Address1,
                shipTo_ContactPerson: formData.shipTo_ContactPerson,
                shipTo_ContactNo: formData.shipTo_ContactNumber,
                remarks: formData.remarks,
                isVatInclusive,
                headerDiscount: parseFloat(formData.headerDiscount) || 0,
                preparedBy: userId,
                status: parseInt(formData.status) || 0,
                isPOPrinted: formData.isPOPrinted,
                reprintCount: parseInt(formData.reprintCount) || 0,
                dateSubmittedForApproval: new Date().toISOString(),
                receivingStatus: formData.receivingStatus,
                departmentId: parseInt(formData.departmentId) || 0,
                locationId: parseInt(formData.locationId) || 0,
                isFromQuotation: inputMode === "quotation" ? 1 : 0,
                isNonVAT: isNonVat,
                paymentMode: formData.paymentMode,
                tin: formData.tin
            };

            const headerResponse = await fetch(`${API_BASE_URL}api/T_PO_Header`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "accept": "text/plain" },
                body: JSON.stringify(headerPayload)
            });

            if (!headerResponse.ok) {
                const errorText = await headerResponse.text();
                throw new Error(`Failed to create PO Header: ${errorText}`);
            }

            const createdHeader = await headerResponse.json();
            const poHeaderId = createdHeader.id;

            const detailsPayload = poItems.map(item => ({
                id: 0, poHeaderId,
                qty: parseFloat(item.qty) || 0,
                uom: item.uom || "",
                itemDescription: item.itemDescription || "",
                specification: item.specification || "",
                itemCode: item.itemCode || "",
                unitPrice: parseFloat(item.unitPrice) || 0,
                detailDiscount: parseFloat(item.detailDiscount) || 0,
                serveStatus: "Pending",
                lastServedDate: new Date().toISOString(),
                chargingVisa: "",
                costEstimate: parseFloat(calculateTotal()) || 0,
                totalReceivedQty: 0,
                referencePRDetailsId: parseInt(item.referencePRDetailsId) || 0,
            }));

            const detailsResponse = await fetch(`${API_BASE_URL}api/T_Po_Details/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "accept": "text/plain" },
                body: JSON.stringify(detailsPayload)
            });

            if (!detailsResponse.ok) {
                const errorText = await detailsResponse.text();
                throw new Error(`Failed to create PO Details: ${errorText}`);
            }

            // ✅ Update UseQty sa QuotationDetails (only if from quotation mode)
            if (inputMode === "quotation" && selectedQuotation) {
                const quotationDetails = selectedQuotation.quotationDetails || selectedQuotation.items || [];
                await Promise.all(poItems.map(async (poItem) => {
                    const matchedDetail = quotationDetails.find(qd =>
                        (qd.prDetailId || qd.PRDetailId) === poItem.referencePRDetailsId
                    );
                    if (!matchedDetail) return;
                    const quotationDetailId = matchedDetail.quotationDetailId || matchedDetail.QuotationDetailId;
                    const qtyToAdd = parseFloat(poItem.qty || 0);
                    try {
                        await fetch(`${API_BASE_URL}api/Quotation/UpdateUseQty/${quotationDetailId}`, {
                            method: "PATCH",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ qtyToAdd })
                        });
                    } catch (err) { console.error(`Error UpdateUseQty:`, err); }
                }));
            }

            await Swal.fire({ icon: "success", title: "Purchase Order Created", text: "The Purchase Order was successfully created.", confirmButtonText: "OK" });

            // Reset
            setFormData({
                poNumber: generatePoNumber(), poCreatedDate: new Date().toISOString().split('T')[0],
                referencePRId: 0, inventoryDestinationPRId: 0, paymentMode: "", tin: "",
                companyId: "", company_Address1: "", company_Address2: "", company_TIN: "",
                company_ContactNumber: "", company_Email: "", departmentId: "", locationId: "",
                vendorId: "", vendor_Address1: "", vendor_Address2: "", vendor_ContactPerson: "",
                vendor_ContactNumber: "", terms: "", shipTo_Location: "", shipTo_Address1: "",
                shipTo_Address2: "", shipTo_ContactPerson: "", shipTo_ContactNumber: "",
                deliveryDate: today, remarks: "", isVatInclusive: true, headerDiscount: 0,
                preparedBy: 0, status: 0, isPOPrinted: false, reprintCount: 0,
                receivingStatus: "Pending", isFromQuotation: 0, isNonVAT: false
            });
            setPoItems([{ id: 0, qty: 0, uom: "", itemDescription: "", specification: "", unitPrice: 0, detailDiscount: 0, serveStatus: "Pending", lastServedDate: new Date().toISOString(), chargingVisa: "", costEstimate: 0, totalReceivedQty: 0, referencePRDetailsId: 0 }]);
            setIsVatInclusive(false);
            setUseDiscount(false);
            setIsNonVat(false);
            setActiveTab("details");
            setSelectedQuotation(null);
            setInputMode("quotation");
            window.location.reload();

        } catch (error) {
            console.error("Full Error:", error);
            await Swal.fire({ icon: "error", title: "Failed to Create PO", text: error.message, confirmButtonText: "OK" });
        }
    };

    const [companyOpen, setCompanyOpen] = useState(false);
    const [companySearch, setCompanySearch] = useState("");

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Company`)
            .then(res => res.json())
            .then(data => setCompanies(data))
            .catch(error => console.error("Error fetching companies:", error));
    }, []);

    const [vendorSearch, setVendorSearch] = useState("");
    const [isVendorOpen, setIsVendorOpen] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Vendor`)
            .then(res => res.json())
            .then(data => setVendors(data))
            .catch(err => console.error("Vendor load error:", err));
    }, []);

    const [showVendorModal, setShowVendorModal] = useState(false);

    const handleVendorSelect = (vendor) => {
        handleChange("vendorId", vendor.id);
        handleChange("vendorName", vendor.VendorName);
        handleChange("tin", vendor.TIN || "");
        handleChange("vendor_Address1", vendor.Address1 || "");
        handleChange("vendor_Address2", vendor.Address2 || "");
        handleChange("vendor_ContactPerson", vendor.ContactPerson || "");
        handleChange("vendor_ContactNumber", vendor.ContactNo || "");
        handleChange("terms", vendor.PaymentTerms || "");
        handleChange("vendor_Email", vendor.Email || "");
        handleChange("vendor_FaxNo", vendor.FaxNo || "");
        setShowVendorModal(false);
    };

    const [isDeptOpen, setIsDeptOpen] = useState(false);
    const [deptSearch, setDeptSearch] = useState("");
    const [isLocationOpen, setIsLocationOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState("");
    const [quotationDateFrom, setQuotationDateFrom] = useState("");
    const [quotationDateTo, setQuotationDateTo] = useState("");

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Department`)
            .then(res => res.json())
            .then(data => setDepartments(data))
            .catch(error => console.error("Error fetching departments:", error));
    }, []);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Location`)
            .then(res => res.json())
            .then(data => setLocations(data))
            .catch(error => console.error("Error fetching locations:", error));
    }, []);

    const [loading, setLoading] = useState(true);
    const [shippingInfo, setShippingInfo] = useState([]);
    const [shippingLocations, setShippingLocations] = useState([]);

    useEffect(() => {
        axios.get(`${API_BASE_URL}api/ShippingInfo`)
            .then(response => {
                const activeLocations = response.data.filter(info => info.isActive === 1);
                setShippingInfo(activeLocations);
                setShippingLocations(activeLocations.map(info => info.shipTo_Location));
            })
            .catch(error => console.error("Error fetching shipping info:", error))
            .finally(() => setLoading(false));
    }, []);

    const handleLocationSelect = (locationName) => {
        handleChange("shipTo_Location", locationName);
        const selected = shippingInfo.find(info => info.shipTo_Location === locationName);
        if (selected) {
            handleChange("shipTo_Address1", selected.shipTo_Address1 || "");
            handleChange("shipTo_Address2", selected.shipTo_Address2 || "");
            handleChange("shipTo_ContactPerson", selected.shipTo_ContactName || "");
            handleChange("shipTo_ContactNumber", selected.shipTo_ContactNo || "");
        }
    };

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (!formData.deliveryDate) handleChange("deliveryDate", today);
    }, []);

    const generatePoNumber = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");
        return `PO-${yyyy}${mm}${dd}-${hh}${min}${ss}`;
    };

    useEffect(() => {
        if (!formData.poNumber) handleChange("poNumber", generatePoNumber());
        if (!formData.poCreatedDate) handleChange("poCreatedDate", today);
    }, []);

    const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
    const confirmDelete = (draftId) => setDeleteConfirmModal(draftId);
    const handleDeleteConfirm = () => {
        if (deleteConfirmModal) {
            deleteDraft(deleteConfirmModal);
            setDeleteConfirmModal(null);
        }
    };

    const [prAvailability, setPrAvailability] = useState({});
    const [pendingQtyCache, setPendingQtyCache] = useState({});

    useEffect(() => {
        const buildPendingCache = async () => {
            try {
                const headersRes = await fetch(`${API_BASE_URL}api/T_PO_Header`);
                if (!headersRes.ok) return;
                const allHeaders = await headersRes.json();
                const pendingHeaders = allHeaders.filter(h => h.status === 0 || h.status === 1);
                if (pendingHeaders.length === 0) return;
                const cache = {};
                await Promise.all(pendingHeaders.map(async (header) => {
                    try {
                        const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${header.id}`);
                        if (!detailsRes.ok) return;
                        const details = await detailsRes.json();
                        details.forEach(detail => {
                            if (detail.referencePRDetailsId) {
                                const key = detail.referencePRDetailsId;
                                cache[key] = (cache[key] || 0) + (detail.qty || 0);
                            }
                        });
                    } catch { }
                }));
                setPendingQtyCache(cache);
            } catch (err) { console.error("Error building pending cache:", err); }
        };
        buildPendingCache();
    }, []);

    useEffect(() => {
        if (prList.length === 0) return;
        const checkAvailability = async () => {
            const result = {};
            const approvedPRs = prList.filter(pr => pr.status === 2);
            await Promise.all(approvedPRs.map(async (pr) => {
                try {
                    const res = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${pr.id}`);
                    const items = await res.json();
                    const availableItems = items.map(item => {
                        const approvedUsed = item.useQTY || 0;
                        const pendingUsed = pendingQtyCache[item.id] || 0;
                        return { itemDescription: item.itemDescription, total: item.qty, approvedUsed, pendingUsed, available: item.qty - approvedUsed - pendingUsed };
                    }).filter(item => item.available > 0);
                    result[pr.id] = { hasAvailable: availableItems.length > 0, items: availableItems };
                } catch { result[pr.id] = { hasAvailable: false, items: [] }; }
            }));
            setPrAvailability(result);
        };
        checkAvailability();
    }, [prList, pendingQtyCache]);

    const [currentPage, setCurrentPage] = useState(1);
    const [quotations, setQuotations] = useState([]);
    const [showQuotationModal, setShowQuotationModal] = useState(false);
    const [quotationSearch, setQuotationSearch] = useState("");
    const [selectedQuotation, setSelectedQuotation] = useState(null);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Quotation`)
            .then(res => res.json())
            .then(data => {
                const arr = Array.isArray(data) ? data : [];
                const selected = arr.filter(q => {
                    const val = q.selected ?? q.Selected ?? q.isSelected ?? false;
                    return val === true || val === 1 || val === "true" || val === "1";
                });
                setQuotations(selected);
            })
            .catch(err => console.error("Error loading Quotations:", err));
    }, []);

    // ✅ Manual vendor name state (for manual mode)
    const [manualVendorName, setManualVendorName] = useState("");

    return (
        <div className="min-h-screen to-blue-50 p-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-2xl p-8 mb-2">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-white flex items-center gap-3">
                            <FileText size={40} />
                            Purchase Order
                        </h1>
                        <p className="text-blue-100 mt-2">Create new purchase order request</p>
                    </div>
                    <div className="text-right">
                        <h2 className="text-2xl font-bold text-white">{formData.poNumber || 'PO-XXXX'}</h2>
                        <p className="text-blue-100 text-sm mt-1">{formData.poCreatedDate || new Date().toLocaleDateString()}</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex bg-white px-6 md:px-8 gap-2 justify-start items-center overflow-x-auto border-b border-gray-200">
                {["details", "items", "remarks"].map((tab, idx) => (
                    <div key={tab} className="flex items-center">
                        {idx > 0 && <span className="text-gray-300 font-bold select-none mr-2">/</span>}
                        <button
                            onClick={() => setActiveTab(tab)}
                            className={`relative px-4 py-3 font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === tab ? "text-blue-700" : "text-gray-500 hover:text-blue-600"}`}
                        >
                            {tab === "details" ? "Company & Vendor Details" : tab === "items" ? "Purchase Order Items" : "Remarks & Approvals"}
                            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 transform skew-x-[-20deg] rounded"></div>}
                        </button>
                    </div>
                ))}
            </div>

            <div className="mb-8">
                {activeTab === "details" && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                        {/* Card 1: Bill To */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2"><Building2 size={18} />Bill To</h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Company: <span className="text-red-500">*</span></label>
                                    <div onClick={() => setCompanyOpen(!companyOpen)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer flex justify-between items-center">
                                        {formData.companyId ? companies.find(c => c.id == formData.companyId)?.companyName : "Select Company"}
                                        <span className="text-gray-500">▼</span>
                                    </div>
                                    {companyOpen && (
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                            <div className="p-2 border-b">
                                                <input type="text" placeholder="Search..." value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500" />
                                            </div>
                                            <div>
                                                {companies.filter((c) => c.companyName?.toLowerCase().includes(companySearch.toLowerCase())).map((company) => (
                                                    <div key={company.id} onClick={() => { handleChange("companyId", company.id); handleChange("company_Address1", company.address1 || ""); handleChange("company_Address2", company.address2 || ""); handleChange("company_TIN", company.tin || ""); handleChange("company_ContactNumber", company.contactNo || ""); handleChange("company_Email", company.email || ""); setCompanyOpen(false); }} className="px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer">{company.companyName}</div>
                                                ))}
                                                {companies.filter((c) => c.companyName.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No results found</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {[{ label: "Address 1:", field: "company_Address1", placeholder: "Street Address" }, { label: "TIN Number:", field: "company_TIN", placeholder: "000-000-000-000" }, { label: "Contact Number:", field: "company_ContactNumber", placeholder: "+63 XXX XXX XXXX" }, { label: "Email:", field: "company_Email", placeholder: "company@email.com", type: "email" }].map(({ label, field, placeholder, type }) => (
                                    <div key={field}>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">{label}</label>
                                        <input type={type || "text"} value={formData[field]} onChange={(e) => handleChange(field, e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder={placeholder} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Card 2: Vendor Information */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2"><User size={18} />Vendor Information</h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Vendor: <span className="text-red-500">*</span></label>

                                    {/* ✅ Kapag MANUAL mode: text input lang, hindi kailangan ng modal */}
                                    {inputMode === "manual" ? (
                                        <input
                                            type="text"
                                            value={formData.vendorName || ""}
                                            onChange={(e) => handleChange("vendorName", e.target.value)}
                                            className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                            placeholder="Type vendor name manually..."
                                        />
                                    ) : (
                                        <div className="px-2 py-2 border text-sm border-gray-300 rounded-lg cursor-pointer bg-white flex justify-between items-center" onClick={() => setShowVendorModal(true)}>
                                            {formData.vendorName || (formData.vendorId ? vendors.find((v) => v.id === formData.vendorId)?.VendorName : "Select Vendor")}                                            <span className="text-gray-500">🔍</span>
                                        </div>
                                    )}

                                    {showVendorModal && inputMode !== "manual" && (
                                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden m-4">
                                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                                                    <h3 className="text-xl font-bold text-white">Select Vendor</h3>
                                                    <button onClick={() => { setShowVendorModal(false); setVendorPage(1); }} className="text-white text-2xl">✕</button>
                                                </div>
                                                <div className="p-4 border-b">
                                                    <input type="text" placeholder="Search vendor..." value={vendorSearch} onChange={(e) => { setVendorSearch(e.target.value); setVendorPage(1); }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                                {(() => {
                                                    const filteredVendors = vendors.filter(v => v.VendorName?.toLowerCase().includes(vendorSearch.toLowerCase()));
                                                    const itemsPerPage = 8;
                                                    const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
                                                    const safePage = Math.min(vendorPage, totalPages || 1);
                                                    const startIndex = (safePage - 1) * itemsPerPage;
                                                    const paginated = filteredVendors.slice(startIndex, startIndex + itemsPerPage);
                                                    return (
                                                        <>
                                                            <div className="flex justify-between items-center px-4 pt-3">
                                                                <p className="text-xs text-gray-500">Showing <span className="font-bold text-blue-600">{filteredVendors.length === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredVendors.length)}</span> of <span className="font-bold">{filteredVendors.length}</span> vendors</p>
                                                                <p className="text-xs text-gray-400">Page {safePage} of {totalPages || 1}</p>
                                                            </div>
                                                            <div className="overflow-y-auto max-h-[45vh] p-4 space-y-2">
                                                                {paginated.length === 0 ? (
                                                                    <div className="text-center py-10"><p className="text-gray-400 text-3xl mb-2">🔍</p><p className="text-gray-500 font-semibold">No vendors found.</p></div>
                                                                ) : (
                                                                    paginated.map((vendor) => (
                                                                        <div key={vendor.id} onClick={() => { handleVendorSelect(vendor); setShowVendorModal(false); setVendorPage(1); }} className="border border-gray-200 rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all">
                                                                            <p className="font-bold text-blue-700">{vendor.VendorName}</p>
                                                                            <p className="text-xs text-gray-500">{vendor.VendorCode} | {vendor.Address1 || "No address"}</p>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                            {totalPages > 1 && (
                                                                <div className="flex justify-center items-center gap-1 px-4 py-3 border-t flex-wrap">
                                                                    <button onClick={() => setVendorPage(1)} disabled={safePage === 1} className="px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">«</button>
                                                                    <button onClick={() => setVendorPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">‹ Prev</button>
                                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1).reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, []).map((p, idx) => p === "..." ? <span key={`dot-${idx}`} className="px-2 text-gray-400 text-xs">...</span> : <button key={p} onClick={() => setVendorPage(p)} className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${safePage === p ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 hover:bg-gray-100 text-gray-700"}`}>{p}</button>)}
                                                                    <button onClick={() => setVendorPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">Next ›</button>
                                                                    <button onClick={() => setVendorPage(totalPages)} disabled={safePage === totalPages} className="px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">»</button>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {[{ label: "Address 1:", field: "vendor_Address1", placeholder: "Street Address" }, { label: "Contact Person:", field: "vendor_ContactPerson", placeholder: "Contact Person Name" }, { label: "Contact Number:", field: "vendor_ContactNumber", placeholder: "+63 XXX XXX XXXX" }, { label: "Tin:", field: "tin", placeholder: "XXX XXX XXXX" }].map(({ label, field, placeholder }) => (
                                    <div key={field}>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">{label}</label>
                                        <input type="text" value={formData[field]} onChange={(e) => handleChange(field, e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder={placeholder} />
                                    </div>
                                ))}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Payment Mode:<span className="text-red-500">*</span></label>
                                    <select value={formData.paymentMode || ""} onChange={(e) => handleChange("paymentMode", e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all">
                                        <option value="">Select Payment Mode</option>
                                        <option value="CASH">CASH</option>
                                        <option value="GCASH">GCASH</option>
                                        <option value="BANK TRANSFER">BANK TRANSFER</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Terms:<span className="text-red-500">*</span></label>
                                    <input type="text" value={formData.terms} onChange={(e) => handleChange("terms", e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="e.g., Net 30, COD, etc." />
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Ship To */}
                        <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2">
                                <h3 className="text-base font-bold text-white flex items-center gap-2"><Truck size={18} />Ship To Details</h3>
                            </div>
                            <div className="p-3 space-y-2">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Location:</label>
                                    <select value={formData.shipTo_Location} onChange={(e) => handleLocationSelect(e.target.value)} className="flex-1 w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all">
                                        <option value="">Select a location</option>
                                        {loading ? <option>Loading...</option> : shippingLocations.map((loc, index) => <option key={index} value={loc}>{loc}</option>)}
                                    </select>
                                </div>
                                {[{ label: "Address 1:", field: "shipTo_Address1", placeholder: "Street Address", green: true }, { label: "Contact Person:", field: "shipTo_ContactPerson", placeholder: "Contact Person Name", green: true }, { label: "Contact Number:", field: "shipTo_ContactNumber", placeholder: "+63 XXX XXX XXXX", green: true }].map(({ label, field, placeholder, green }) => (
                                    <div key={field}>
                                        <label className={`block text-xs font-bold mb-1 ${green ? "text-green-600" : "text-gray-700"}`}>{label} {green && <span className="text-red-500">*</span>}</label>
                                        <input type="text" value={formData[field]} onChange={(e) => handleChange(field, e.target.value)} className={`w-full px-2 py-2 text-sm border rounded-lg focus:ring-1 transition-all ${green ? "border-green-300 focus:ring-green-500 focus:border-green-500" : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"}`} placeholder={placeholder} />
                                    </div>
                                ))}
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Delivery Date:</label>
                                    <input type="date" value={formData.deliveryDate || today} onChange={(e) => handleChange("deliveryDate", e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                                </div>
                            </div>
                        </div>

                        {/* ✅ Reference Quotation with Toggle */}
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-xl shadow-md border border-blue-100 p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                                    <label className="block text-sm font-bold text-gray-700">
                                        Reference Quotation <span className="text-red-500">*</span>
                                    </label>

                                    {/* ✅ Toggle Buttons */}
                                    {/* Toggle Buttons — left-aligned */}
                                    <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1 w-fit">
                                        <button
                                            onClick={() => {
                                                setInputMode("quotation");
                                                handleChange("isFromQuotation", 1);
                                            }}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${inputMode === "quotation"
                                                ? "bg-blue-600 text-white shadow-md"
                                                : "text-gray-600 hover:bg-gray-200"
                                                }`}
                                        >
                                            {/* Document icon */}
                                            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="2" y="1" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
                                                <line x1="4.5" y1="5" x2="9.5" y2="5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                                                <line x1="4.5" y1="7.5" x2="9.5" y2="7.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                                                <line x1="4.5" y1="10" x2="7.5" y2="10" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                                            </svg>
                                            From Quotation
                                        </button>
                                        <button
                                            onClick={() => {
                                                setInputMode("manual");
                                                handleChange("isFromQuotation", 0);
                                                setSelectedQuotation(null);
                                                handleChange("vendorId", "");
                                                handleChange("vendorName", "");
                                            }}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${inputMode === "manual"
                                                ? "bg-orange-500 text-white shadow-md"
                                                : "text-gray-600 hover:bg-gray-200"
                                                }`}
                                        >
                                            {/* Pencil icon */}
                                            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10.5 2.5L13 5L6 12H3.5V9.5L10.5 2.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                                <line x1="8.5" y1="4.5" x2="11" y2="7" stroke="currentColor" strokeWidth="1.1" />
                                            </svg>
                                            Manual Input
                                        </button>
                                    </div>
                                </div>

                                {/* ✅ Mode Description Badge */}
                                <div className={`mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${inputMode === "quotation"
                                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                                    : "bg-orange-50 text-orange-700 border border-orange-200"
                                    }`}>
                                    {inputMode === "quotation"
                                        ? "📋 Items will be pulled from selected quotation"
                                        : "✏️ Manual mode — enter vendor & items manually, no quotation needed"}
                                </div>

                                {/* ✅ Quotation Selector (only shown if "From Quotation" mode) */}
                                {inputMode === "quotation" && (
                                    <div
                                        onClick={() => setShowQuotationModal(true)}
                                        className="px-3 py-2.5 border text-sm border-gray-300 rounded-lg cursor-pointer bg-white flex justify-between items-center hover:border-blue-500 transition-all"
                                    >
                                        <span className={selectedQuotation ? "text-gray-800 font-semibold" : "text-gray-400"}>
                                            {selectedQuotation
                                                ? `${selectedQuotation.quotationNo} — ${selectedQuotation.supplierName}`
                                                : "Click to select a quotation..."}
                                        </span>
                                        <span className="text-gray-500">🔍</span>
                                    </div>
                                )}

                                {/* ✅ Manual mode info */}
                                {inputMode === "manual" && (
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                                        <p className="font-semibold mb-1">🟠 Manual Input Mode Active</p>
                                        <p className="text-xs">You can now enter vendor details manually above and add items manually in the <strong>Purchase Order Items</strong> tab. No quotation reference required.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Quotation Selection Modal */}
                {showQuotationModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden m-4">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                                <h3 className="text-xl font-bold text-white">Select Quotation</h3>
                                <button onClick={() => setShowQuotationModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                            </div>
                            <div className="p-4 border-b space-y-2">
                                <input type="text" placeholder="Search by Quote No. or Supplier..." value={quotationSearch} onChange={(e) => { setQuotationSearch(e.target.value); setCurrentPage(1); }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                <div className="flex gap-2 items-center">
                                    <div className="flex-1">
                                        <label className="text-xs font-bold text-gray-600 mb-1 block">Valid From:</label>
                                        <input type="date" value={quotationDateFrom} onChange={(e) => { setQuotationDateFrom(e.target.value); setCurrentPage(1); }} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-xs font-bold text-gray-600 mb-1 block">Valid Until:</label>
                                        <input type="date" value={quotationDateTo} onChange={(e) => { setQuotationDateTo(e.target.value); setCurrentPage(1); }} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div className="flex items-end pb-0.5">
                                        <button onClick={() => { setQuotationDateFrom(""); setQuotationDateTo(""); setCurrentPage(1); }} className="px-3 py-2 text-xs bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold text-gray-600 transition-all">Clear</button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[50vh]">
                                {(() => {
                                    const filtered = quotations.filter(q => {
                                        const matchSearch = (q.quotationNo || "").toLowerCase().includes(quotationSearch.toLowerCase()) || (q.supplierName || "").toLowerCase().includes(quotationSearch.toLowerCase());
                                        const validUntil = (q.validUntil || q.ValidUntil || "").split("T")[0];
                                        const matchFrom = quotationDateFrom ? validUntil >= quotationDateFrom : true;
                                        const matchTo = quotationDateTo ? validUntil <= quotationDateTo : true;
                                        const items = q.quotationDetails || q.items || [];
                                        const hasAvailable = items.some(d => { const qty = d.qty || d.Qty || 0; const useQty = d.useQty || d.UseQty || 0; return (qty - useQty) > 0; });
                                        return matchSearch && matchFrom && matchTo && hasAvailable;
                                    });
                                    const itemsPerPage = 5;
                                    const totalPages = Math.ceil(filtered.length / itemsPerPage);
                                    const safePage = Math.min(currentPage, totalPages || 1);
                                    const startIndex = (safePage - 1) * itemsPerPage;
                                    const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);
                                    if (filtered.length === 0) return <div className="text-center py-10"><p className="text-gray-400 text-4xl mb-3">📭</p><p className="text-gray-500 font-semibold">No available quotations found.</p></div>;
                                    return (
                                        <>
                                            <div className="flex justify-between items-center mb-3">
                                                <p className="text-xs text-gray-500">Showing <span className="font-bold text-blue-600">{startIndex + 1}–{Math.min(startIndex + itemsPerPage, filtered.length)}</span> of <span className="font-bold">{filtered.length}</span> quotations</p>
                                                <p className="text-xs text-gray-400">Page {safePage} of {totalPages}</p>
                                            </div>
                                            <div className="space-y-2">
                                                {paginated.map((q) => {
                                                    const items = q.quotationDetails || q.items || [];
                                                    const itemSummary = items.map(d => ({ itemCode: d.itemCode || d.ItemCode || "", itemDescription: d.itemDescription || d.ItemDescription || "", qty: d.qty || d.Qty || 0, useQty: d.useQty || d.UseQty || 0, available: (d.qty || d.Qty || 0) - (d.useQty || d.UseQty || 0) }));
                                                    const availableItems = itemSummary.filter(i => i.available > 0);
                                                    return (
                                                        <div key={q.quotationId || q.QuotationId} onClick={() => handleQuotationSelect(q)} className="border border-gray-200 rounded-lg p-4 transition-all hover:bg-blue-50 hover:border-blue-300 cursor-pointer">
                                                            <div className="flex justify-between items-start">
                                                                <div className="flex-1">
                                                                    <p className="font-bold text-blue-700 text-lg">{q.quotationNo || q.QuotationNo}</p>
                                                                    <p className="text-sm text-gray-700 font-semibold mt-0.5">{q.supplierName || q.SupplierName}</p>
                                                                    <p className="text-xs text-gray-500 mt-1">PR #{q.prHeaderId || q.PRHeaderId} &nbsp;•&nbsp; Valid until: {(q.validUntil || q.ValidUntil || "").split("T")[0] || "—"}</p>
                                                                    {(q.paymentTerms || q.PaymentTerms) && <p className="text-xs text-gray-500">Terms: {q.paymentTerms || q.PaymentTerms}{(q.deliveryDays || q.DeliveryDays) && ` • Delivery: ${q.deliveryDays || q.DeliveryDays} days`}</p>}
                                                                    <div className="mt-2 space-y-1">
                                                                        {availableItems.map((item, idx) => (
                                                                            <div key={idx} className="flex items-center gap-2 flex-wrap">
                                                                                <span className="text-xs font-bold text-gray-600">{item.itemCode} — {item.itemDescription}</span>
                                                                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Qty: {item.qty}</span>
                                                                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Used: {item.useQty}</span>
                                                                                <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Available: {item.available}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <div className="mt-2"><div className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-2 py-1 rounded-full"><span>✓</span> Selected Quotation</div></div>
                                                                </div>
                                                                <div className="text-right ml-3 flex-shrink-0">
                                                                    <p className="text-xs text-gray-400">Grand Total</p>
                                                                    <p className="font-black text-green-600 text-lg">₱{Number(q.grandTotal || q.GrandTotal || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</p>
                                                                    <p className="text-xs text-gray-400 mt-1">{availableItems.length}/{itemSummary.length} items available</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {totalPages > 1 && (
                                                <div className="flex justify-center items-center gap-1 mt-4 pt-4 border-t flex-wrap">
                                                    <button onClick={() => setCurrentPage(1)} disabled={safePage === 1} className="px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">«</button>
                                                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">‹ Prev</button>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1).reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, []).map((p, idx) => p === "..." ? <span key={`dot-${idx}`} className="px-2 text-gray-400 text-xs">...</span> : <button key={p} onClick={() => setCurrentPage(p)} className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${safePage === p ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 hover:bg-gray-100 text-gray-700"}`}>{p}</button>)}
                                                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">Next ›</button>
                                                    <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages} className="px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold">»</button>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* PR Details Modal */}
            {prDetailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[80vh] overflow-hidden m-4">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">Select Items from {formData.referencePRNo}</h3>
                            <button onClick={() => setPrDetailsModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <table className="w-full">
                                <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                        <th className="p-3 text-left"><input type="checkbox" checked={Object.values(checkedItems).every(v => v)} onChange={(e) => { const newChecked = {}; prDetails.forEach(item => { newChecked[item.id] = e.target.checked; }); setCheckedItems(newChecked); }} className="w-5 h-5" /></th>
                                        <th className="p-3 text-left font-bold">Item Code</th>
                                        <th className="p-3 text-left font-bold">Original Qty</th>
                                        <th className="p-3 text-left font-bold">Used Qty</th>
                                        <th className="p-3 text-left font-bold text-green-600">Available</th>
                                        <th className="p-3 text-left font-bold text-blue-600">To Use</th>
                                        <th className="p-3 text-left font-bold">UOM</th>
                                        <th className="p-3 text-left font-bold">Item Description</th>
                                        <th className="p-3 text-left font-bold">Specification</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prDetails.filter((item) => item.actualAvailable > 0).map((item) => {
                                        const availableQty = item.actualAvailable;
                                        return (
                                            <tr key={item.id} className="border-b hover:bg-gray-50">
                                                <td className="p-3"><input type="checkbox" checked={checkedItems[item.id] || false} onChange={(e) => setCheckedItems({ ...checkedItems, [item.id]: e.target.checked })} className="w-5 h-5" /></td>
                                                <td className="p-3 text-gray-600">{item.itemCode}</td>
                                                <td className="p-3 text-gray-600">{item.qty}</td>
                                                <td className="p-3">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-orange-600 font-semibold">{item.displayUsedQty || 0}</span>
                                                        {item.approvedUsed > 0 && <span className="text-xs text-blue-600 whitespace-nowrap">✅ {item.approvedUsed} approved</span>}
                                                        {item.pendingUsed > 0 && <span className="text-xs text-orange-500 whitespace-nowrap">⏳ {item.pendingUsed} pending</span>}
                                                    </div>
                                                </td>
                                                <td className="p-3"><span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-bold">{availableQty}</span></td>
                                                <td className="p-3">
                                                    <input type="number" min="0" max={availableQty} value={item.selectedQty || 0}
                                                        onChange={(e) => {
                                                            const inputValue = parseFloat(e.target.value) || 0;
                                                            if (inputValue > availableQty) { Swal.fire({ icon: 'warning', title: 'Exceeded Available Quantity', text: `Only ${availableQty} available. You entered ${inputValue}.`, timer: 2000, showConfirmButton: false }); return; }
                                                            setPrDetails(prDetails.map(d => d.id === item.id ? { ...d, selectedQty: inputValue } : d));
                                                        }}
                                                        className="w-24 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 font-semibold" />
                                                </td>
                                                <td className="p-3"><span className="px-2 py-1 text-sm font-medium text-gray-800">{item.uom || "—"}</span></td>
                                                <td className="p-3">{item.itemDescription}</td>
                                                <td className="p-3">{item.materialFinish}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 bg-gray-50 flex justify-end gap-3">
                            <button onClick={() => setPrDetailsModal(false)} className="px-6 py-2 bg-gray-300 rounded-lg hover:bg-gray-400">Cancel</button>
                            <button
                                onClick={async () => {
                                    try {
                                        const selected = prDetails.filter(item => checkedItems[item.id]);
                                        if (selected.length === 0) { await Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one item.', confirmButtonText: 'OK' }); return; }
                                        if (selected.some(item => !item.selectedQty || item.selectedQty <= 0)) { await Swal.fire({ icon: 'warning', title: 'Invalid Quantity', text: 'Please enter a valid quantity for all selected items.', confirmButtonText: 'OK' }); return; }
                                        const mappedItems = selected.map(item => ({ id: 0, qty: item.selectedQty, uom: item.uom, itemDescription: item.itemDescription, specification: item.materialFinish, itemCode: item.itemCode || "", unitPrice: item.unitPrice || 0, detailDiscount: 0, serveStatus: "Pending", lastServedDate: new Date().toISOString(), chargingVisa: "", costEstimate: 0, totalReceivedQty: 0, referencePRDetailsId: item.id }));
                                        setPoItems(mappedItems);
                                        setPrDetailsModal(false);
                                        setActiveTab("items");
                                        await Swal.fire({ icon: 'success', title: 'Items Loaded', text: `${mappedItems.length} items loaded successfully!`, timer: 2000, showConfirmButton: false });
                                    } catch (error) {
                                        await Swal.fire({ icon: 'error', title: 'Failed', text: 'Failed to process items.', confirmButtonText: 'OK' });
                                    }
                                }}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                            >
                                Proceed to Items ({Object.values(checkedItems).filter(v => v).length} selected)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Items Tab */}
            {activeTab === "items" && (
                <div className="max-w-8xl mx-auto space-y-6">
                    <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/ shadow-xl">
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">Purchase Order Item Details</h3>
                        <p className="text-gray-600">
                            {inputMode === "manual"
                                ? "Manual mode — add items freely below."
                                : "Items loaded from selected quotation."}
                        </p>
                    </div>
                    <div className="bg-white/70 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gradient-to-r from-blue-500/10 via-blue-500/10 to-purple-500/10 backdrop-blur-sm">
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Ref. Item Code</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Qty</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">UOM</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Item Description</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Specification</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Unit Price</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Discount %</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-800 uppercase tracking-wider">Total</th>
                                        <th className="px-6 py-4 text-center text-sm font-bold text-gray-800 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200/50">
                                    {poItems.map((item, index) => {
                                        const lineTotal = item.qty * item.unitPrice;
                                        const discountAmount = useDiscount ? (lineTotal * item.detailDiscount) / 100 : 0;
                                        const total = lineTotal - discountAmount;
                                        // ✅ Manual mode = all fields editable; Quotation mode = most fields readonly
                                        const isManual = inputMode === "manual";
                                        return (
                                            <tr key={index} className="hover:bg-white/50 transition-all duration-200">
                                                <td className="px-6 py-4">
                                                    <input type="text" value={item.itemCode || ""} disabled={!isManual}
                                                        onChange={(e) => isManual && updateItem(index, "itemCode", e.target.value)}
                                                        className={`w-full px-4 py-2.5 border rounded-xl text-sm ${isManual ? "bg-white border-gray-300 focus:ring-2 focus:ring-blue-500" : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"}`}
                                                        placeholder="Item Code" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input type="number" value={item.qty || 0} disabled={!isManual}
                                                        onChange={(e) => isManual && updateItem(index, "qty", e.target.value)}
                                                        className={`w-24 px-4 py-2.5 border rounded-xl text-sm ${isManual ? "bg-white border-gray-300 focus:ring-2 focus:ring-blue-500" : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"}`} />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <select value={item.uom || ""} disabled={!isManual}
                                                        onChange={(e) => isManual && updateItem(index, "uom", e.target.value)}
                                                        className={`w-28 px-3 py-2.5 border rounded-xl text-sm ${isManual ? "bg-white border-gray-300 focus:ring-2 focus:ring-blue-500" : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"}`}>
                                                        <option value="">UOM</option>
                                                        {uoms.map((u) => <option key={u.id} value={u.uom}>{u.uom}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <textarea value={item.itemDescription || ""} disabled={!isManual}
                                                        onChange={(e) => isManual && updateItem(index, "itemDescription", e.target.value)}
                                                        className={`w-full px-4 py-2.5 border rounded-xl text-sm resize-none ${isManual ? "bg-white border-gray-300 focus:ring-2 focus:ring-blue-500" : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"}`}
                                                        rows="2" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <textarea value={item.specification || ""} disabled={!isManual}
                                                        onChange={(e) => isManual && updateItem(index, "specification", e.target.value)}
                                                        className={`w-full px-4 py-2.5 border rounded-xl text-sm resize-none ${isManual ? "bg-white border-gray-300 focus:ring-2 focus:ring-blue-500" : "bg-gray-100 border-gray-200 cursor-not-allowed text-gray-500"}`}
                                                        rows="2" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input type="number" step="0.01" value={item.unitPrice || 0}
                                                        onChange={(e) => updateItem(index, "unitPrice", e.target.value)}
                                                        className="w-32 px-4 py-2.5 bg-white/80 border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/50 text-sm" />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <input type="number" step="0.01" value={item.detailDiscount || 0}
                                                                onChange={(e) => updateItem(index, "detailDiscount", e.target.value)}
                                                                disabled={!useDiscount}
                                                                className="w-24 px-4 py-2.5 bg-white/80 border border-gray-200/50 rounded-xl disabled:bg-gray-100 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500/50 text-sm" />
                                                            <span className="text-sm font-semibold text-gray-600">%</span>
                                                        </div>
                                                        {useDiscount && discountAmount > 0 && <span className="text-xs text-red-500 font-semibold">-₱{discountAmount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-lg font-bold text-gray-900">₱{total.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => removeItem(index)} className="w-10 h-10 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl hover:from-red-600 hover:to-rose-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-110 font-bold text-lg">×</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* ✅ Add Item button — only shown in Manual mode */}
                        {inputMode === "manual" && (
                            <div className="px-6 py-4 border-t border-gray-100">
                                <button
                                    onClick={addNewItem}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-md font-semibold text-sm"
                                >
                                    ＋ Add Item
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/60 shadow-xl">
                            <h4 className="text-xl font-bold text-gray-900 mb-6">VAT & Discount Options</h4>
                            <div className="space-y-4">
                                {[{ label: "Is VAT Inclusive?", checked: isVatInclusive, onChange: (v) => { setIsVatInclusive(v); if (v) setIsNonVat(false); } }, { label: "Use Discount?", checked: useDiscount, onChange: setUseDiscount }, { label: "Is NON-VAT?", checked: isNonVat, onChange: (v) => { setIsNonVat(v); if (v) setIsVatInclusive(false); } }].map(({ label, checked, onChange }) => (
                                    <label key={label} className="flex items-center gap-4 cursor-pointer p-4 rounded-xl hover:bg-white/50 transition-all group">
                                        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all" />
                                        <span className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white/70 backdrop-blur-2xl rounded-3xl p-8 border border-white/60 shadow-xl">
                            <h4 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h4>
                            <div className="space-y-4">
                                {[{ label: "SubTotal (VAT-Ex):", value: `₱${calculateSubtotal().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, color: "" }, { label: "VAT Amount:", value: `₱${calculateVAT().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, color: "" }, { label: "Disc. Amount:", value: `-₱${calculateTotalDiscount().toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, color: "text-red-500" }].map(({ label, value, color }) => (
                                    <div key={label} className="flex justify-between items-center p-4 bg-white/50 rounded-xl">
                                        <span className="font-semibold text-gray-700">{label}</span>
                                        <span className={`font-mono font-bold text-lg ${color || "text-gray-900"}`}>{value}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center p-5 bg-gradient-to-r from-blue-500/20 to-blue-500/20 rounded-2xl border-2 border-blue-300/50 mt-6">
                                    <span className="font-bold text-xl text-gray-900">NET AMOUNT</span>
                                    <span className="font-mono font-bold text-2xl text-blue-600">₱{calculateTotal().toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Draft Modal */}
            {showDraftModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">Load Draft</h3>
                            <button onClick={() => setShowDraftModal(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
                        </div>
                        <div className="p-4">
                            {savedDrafts.length === 0 ? <p className="text-gray-500 text-center py-8">No saved drafts found.</p> : (
                                <div className="space-y-2">
                                    {savedDrafts.map((draft) => (
                                        <div key={draft.id} className="border border-blue-200 rounded-lg p-4 hover:bg-blue-50 transition-all">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <p className="font-bold text-blue-700">{draft.formData.poNumber}</p>
                                                    <p className="text-sm text-gray-600">{new Date(draft.timestamp).toLocaleString()}</p>
                                                    <p className="text-xs text-gray-500 mt-1">Items: {draft.poItems.length} | Vendor: {draft.formData.vendorId ? "Selected" : "Not selected"}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => loadDraft(draft)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">Load</button>
                                                    <button onClick={() => confirmDelete(draft.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold transition-all">Delete</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 p-4"><h3 className="text-xl font-bold text-white">⚠️ Confirm Delete</h3></div>
                        <div className="p-6">
                            <p className="text-gray-700 mb-6">Are you sure you want to delete this draft? This action cannot be undone.</p>
                            <div className="flex gap-3 justify-end">
                                <button onClick={() => setDeleteConfirmModal(null)} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-all font-semibold">Cancel</button>
                                <button onClick={handleDeleteConfirm} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold">Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remarks Tab */}
            {activeTab === "remarks" && (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">PO Remarks/Special Instructions</label>
                        <textarea className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={4} placeholder="Enter remarks here..." value={formData.remarks} onChange={(e) => handleInputChange(e, "remarks")} />
                    </div>
                </div>
            )}

            {/* Navigation */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6 px-4 md:px-8">
                <div className="flex gap-3">
                    <button onClick={() => { if (activeTab === "items") setActiveTab("details"); if (activeTab === "remarks") setActiveTab("items"); }} disabled={activeTab === "details"} className={`px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "details" ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>← Previous</button>
                    <button onClick={() => { if (activeTab === "details") setActiveTab("items"); if (activeTab === "items") setActiveTab("remarks"); }} disabled={activeTab === "remarks"} className={`px-6 py-3 rounded-lg font-semibold transition-all ${activeTab === "remarks" ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}>Next →</button>
                </div>
                <div className="text-sm text-slate-600 font-semibold">Step {activeTab === "details" ? "1" : activeTab === "items" ? "2" : "3"} of 3</div>
                <div className="flex flex-wrap gap-3">
                    <button onClick={saveDraft} className="px-6 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all font-semibold shadow-lg">💾 Save Draft</button>
                    <button onClick={() => setShowDraftModal(true)} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-700 text-white rounded-xl hover:from-purple-700 hover:to-blue-800 transition-all font-semibold shadow-lg">📂 Load Draft</button>
                    <button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg">Create Purchase Order</button>
                </div>
            </div>
        </div>
    );
}